import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { StickyNav, type StickyNavSection } from '../../components/ui/StickyNav';
import { effectiveMetaAdType } from '../../lib/effectiveMetaAdType';
import { listCampaigns } from '../campaigns/api';
import { GeneratePreviewModal, SendPreviewModal, SendResultToast } from '../preview';
import type { SendResult } from '../preview';
import { getRolloutRows, listUsers } from './api';
import { BuildCampaignModal } from './BuildCampaignModal';
import { BuildViewBody } from './BuildViewBody';
import { buildDriftFlags } from './BuildViewModificationBadges';
import { BuildViewNavBar } from './BuildViewNavBar';
import { activeChannelsForRow } from '../../lib/platformEnabled';

// P2b-3 Build View — per-row focused build page for Meta and Google
// teams. See docs/P2b-3_BuildView_Spec.md.
//
// Same-shape route for both platforms; the platform prop drives the
// platform-specific bits (resolved name vs label, content blocks,
// execution checkboxes). Loads the same data as the role grids
// (rollout + rows + accounts + locationProfiles + brandProfile +
// campaigns + users) and renders one row's worth at a time.
//
// Data path matches the grids exactly so react-query dedupes when
// a buyer arrives via the View icon — the master query is already
// in cache from the grid render. On hard-refresh / direct deep link,
// it fetches fresh.
//
// Filtered list for Prev/Next is read from location.state — the grid
// passes its post-filter, post-sort row IDs at navigation time.
// Missing state (hard refresh, deep link) → Prev/Next disabled with
// tooltip; row content still loads from the row payload.

type Props = { platform: 'meta' | 'google' };

type LocationState =
  | { filteredRowIds?: unknown }
  | null
  | undefined;

export function BuildViewPage({ platform }: Props) {
  const { id, rowId } = useParams<{ id: string; rowId: string }>();
  const location = useLocation();
  // Ad Preview Mockups Stage 1 §3.1. Hook hoisted above the early-return
  // branches so the hook order stays stable across loading → loaded
  // transitions. Modal renders at the bottom of the JSX conditionally.
  const [previewOpen, setPreviewOpen] = useState(false);
  // Ad Preview Stage 3 §3.1: single-send modal + result toast.
  const [sendOpen, setSendOpen] = useState(false);
  const [sendResult, setSendResult] = useState<SendResult | null>(null);
  // Build Campaign (Phase 1): rollout → pre-filled Meta Ad Builder draft.
  const [buildCampaignOpen, setBuildCampaignOpen] = useState(false);

  // Optional Prev/Next list passed via location.state at navigation
  // time. Defensive parse — treat missing / malformed shapes as "no
  // list, navigation disabled".
  const filteredRowIds = parseFilteredRowIds(location.state);

  // Data — same query key as the role grid the buyer came from, so
  // a cached fetch dedupes. No gridFilter on this query because the
  // Build View also needs to surface rows the gate hasn't accepted
  // (e.g. an AM is mid-build and a budget hasn't been authored yet).
  // The grid's View icon won't navigate to ungated rows, but a deep-
  // link from outside should still resolve.
  const rowsQuery = useQuery({
    queryKey: ['rollout', id, 'rows'],
    queryFn: () => getRolloutRows(id!),
    enabled: !!id,
    refetchOnWindowFocus: true,
  });

  const brandAccountId = rowsQuery.data?.rollout.brandAccountId;
  const campaignsQuery = useQuery({
    queryKey: ['campaigns', 'brand', brandAccountId],
    queryFn: () => listCampaigns({ brandAccountId: brandAccountId! }),
    enabled: !!brandAccountId,
  });
  const usersQuery = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => listUsers(),
  });

  const allCampaigns = campaignsQuery.data ?? [];
  const allUsers = usersQuery.data ?? [];

  if (
    rowsQuery.isLoading ||
    (brandAccountId && campaignsQuery.isLoading) ||
    usersQuery.isLoading
  ) {
    return <CenteredSpinner />;
  }
  if (rowsQuery.error || !rowsQuery.data) {
    return (
      <div className="text-red-600 text-sm">
        Failed to load Build View:{' '}
        {(rowsQuery.error as Error | undefined)?.message ?? 'not found'}
      </div>
    );
  }

  const { rollout, rows, accounts, locationProfiles, brandFranchiseProfile } =
    rowsQuery.data;
  const row = rows.find((r) => r.id === rowId) ?? null;

  if (!row) {
    return <RowNotFound rolloutId={rollout.id} platform={platform} />;
  }

  const account = accounts.find((a) => a.id === row.accountId);
  if (!account) {
    return <RowNotFound rolloutId={rollout.id} platform={platform} />;
  }

  const locationProfile =
    locationProfiles.find((p) => p.accountId === account.id) ?? null;
  const campaign = row.campaignId
    ? allCampaigns.find((c) => c.id === row.campaignId) ?? null
    : null;
  const archived = rollout.status === 'archived';
  const platformLabel = platform === 'meta' ? 'Meta' : 'Google';
  const locationLabel = locationProfile?.franchiseSuburb?.trim() || account.name;
  const headerDrift = buildDriftFlags(row, campaign, platform);

  // Build View polish (Fix 3): build the side-menu section list per
  // platform. Lead Form is gated by effective ad type — only included
  // on Meta when the row resolves to lead_gen. Order tracks
  // BuildViewBody's render order exactly so scroll-spy reads top-down.
  const sideMenuSections = buildSideMenuSections({
    platform,
    row,
    campaign,
    locationProfile,
  });

  const existingPreviewId = row.latestPreviewLinkId ?? null;
  const hasPreviewableContent = row.rowType === 'campaign' && !!row.campaignId;
  const previewButtonLabel = existingPreviewId
    ? 'Re-generate Preview'
    : 'Generate Preview';
  // Build Campaign (Phase 1): Meta only, on a real campaign row. Opens the Meta Ad
  // Builder pre-filled from this rollout row + the account-default audiences.
  const canBuildCampaign =
    platform === 'meta' && row.rowType === 'campaign' && !!row.campaignId;

  return (
    <div className="mx-auto max-w-5xl grid grid-cols-[200px_minmax(0,1fr)] gap-8">
      <StickyNav
        sections={sideMenuSections}
        ariaLabel="Build View sections"
        // Ad Preview Mockups Stage 1 §3.1: Generate Preview lives in the
        // sticky nav so it's always one click away as the buyer scrolls.
        // Hidden on rows that can't render a preview (unassigned /
        // opt-out) — the modal would 400 server-side anyway.
        actions={
          hasPreviewableContent || canBuildCampaign ? (
            <div className="space-y-2">
              {canBuildCampaign && (
                <Button
                  type="button"
                  className="w-full"
                  onClick={() => setBuildCampaignOpen(true)}
                  title="Open the Meta Ad Builder pre-filled from this rollout"
                >
                  Build Campaign
                </Button>
              )}
              {hasPreviewableContent && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setPreviewOpen(true)}
                  title="Generate a public preview URL for this row"
                >
                  {previewButtonLabel}
                </Button>
              )}
              {/* Ad Preview Stage 3 §3.1: Send Preview coexists with
                  Generate Preview. Disabled with a tooltip until a
                  preview link exists -- there's nothing to send
                  otherwise (approved decision). */}
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => setSendOpen(true)}
                disabled={!existingPreviewId}
                title={
                  existingPreviewId
                    ? 'Email / SMS this preview to the franchisee contacts'
                    : 'Generate a preview first, then you can send it.'
                }
              >
                Send Preview
              </Button>
            </div>
          ) : null
        }
      />

      <div className="space-y-6 min-w-0">
        <header className="space-y-3">
          <div className="flex items-center justify-between gap-4 text-sm">
            <Link
              to={`/rollouts/${rollout.id}/grid/${platform}`}
              className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 hover:underline"
            >
              ← Back to {platformLabel} grid
            </Link>
            <BuildViewNavBar
              rolloutId={rollout.id}
              rowId={row.id}
              platform={platform}
              filteredRowIds={filteredRowIds}
            />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold text-slate-900">
              {locationLabel} — {platformLabel} Build
            </h1>
            {(headerDrift.anyContent || headerDrift.dateAny) && (
              <span
                className="text-xs uppercase tracking-wide text-orange-700 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded inline-flex items-center gap-1.5"
                title="Row content or dates modified from Campaign — copy carefully"
              >
                <span aria-hidden className="w-2 h-2 rounded-full bg-orange-500" />
                Customised
              </span>
            )}
            {archived && (
              <span className="text-xs uppercase tracking-wide text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                Read-only (archived)
              </span>
            )}
          </div>
        </header>

        <BuildViewBody
          rollout={rollout}
          row={row}
          account={account}
          accounts={accounts}
          locationProfile={locationProfile}
          brandFranchiseProfile={brandFranchiseProfile}
          campaign={campaign}
          users={allUsers}
          archived={archived}
          platform={platform}
        />

        {/* §2.11 Footer navigation — same Prev/Next as the header,
            duplicated at the bottom for convenience after buyers scroll
            through the section stack. */}
        <footer className="flex items-center justify-end pt-2">
          <BuildViewNavBar
            rolloutId={rollout.id}
            rowId={row.id}
            platform={platform}
            filteredRowIds={filteredRowIds}
          />
        </footer>
      </div>

      {/* Ad Preview Mockups Stage 1 §3.1: modal renders at the root of
          the page so it overlays the StickyNav + content grid. Opens
          with `existingLinkId` so the modal pre-fetches the row's
          current latest preview when there is one. */}
      {previewOpen && (
        <GeneratePreviewModal
          rolloutId={rollout.id}
          rowId={row.id}
          locationName={locationLabel}
          existingLinkId={existingPreviewId}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      {/* Ad Preview Stage 3 §3.1: single-send modal + result toast. */}
      {sendOpen && (
        <SendPreviewModal
          rolloutId={rollout.id}
          rowId={row.id}
          locationAccountId={row.accountId}
          locationName={locationLabel}
          rowChannels={activeChannelsForRow({
            campaignChannels: campaign?.channels ?? null,
            accountRunsMeta: account.runsMeta,
            accountRunsGoogle: account.runsGoogle,
            rowMetaEnabled: row.metaEnabled,
            rowGoogleEnabled: row.googleEnabled,
          })}
          onClose={() => setSendOpen(false)}
          onSent={(result) => setSendResult(result)}
        />
      )}
      {sendResult && (
        <SendResultToast result={sendResult} onDismiss={() => setSendResult(null)} />
      )}

      {/* Build Campaign (Phase 1): rollout → pre-filled Meta Ad Builder draft. */}
      {buildCampaignOpen && (
        <BuildCampaignModal
          rollout={rollout}
          row={row}
          account={account}
          locationProfile={locationProfile}
          brandFranchiseProfile={brandFranchiseProfile}
          campaign={campaign}
          onClose={() => setBuildCampaignOpen(false)}
        />
      )}
    </div>
  );
}

// Build the side-menu section list for the active platform. Mirrors
// the section render order in BuildViewBody (post-Fix-2 reorder):
// Context → Ad Account → Resolved Name → Budget & Dates → Content
// blocks → Landing URL → Assets → UTM → Execution. Lead Form (Meta
// only) is conditionally included when the effective ad type
// resolves to lead_gen — when it isn't rendered in the body, it
// shouldn't appear in the menu either (clicking would scroll to
// nothing).
function buildSideMenuSections(opts: {
  platform: 'meta' | 'google';
  row: import('../../types/accountCampaign').AccountCampaign;
  campaign: import('../../types/campaign').Campaign | null;
  locationProfile: import('../../types/franchiseLocationProfile').FranchiseLocationProfile | null;
}): StickyNavSection[] {
  const { platform, row, campaign, locationProfile } = opts;
  const sections: StickyNavSection[] = [
    { id: 'bv-context', label: 'Context' },
    { id: 'bv-ad-account', label: 'Ad Account' },
    {
      id: 'bv-resolved-name',
      label: platform === 'meta' ? 'Resolved Name' : 'Resolved Ad Label',
    },
    { id: 'bv-budget-dates', label: 'Budget & Dates' },
  ];
  if (platform === 'meta') {
    sections.push(
      { id: 'bv-body-copy', label: 'Body Copy' },
      { id: 'bv-headlines', label: 'Headlines' },
      { id: 'bv-descriptions', label: 'Descriptions' },
      { id: 'bv-cta', label: 'CTA' },
    );
    const adType = effectiveMetaAdType(
      { metaAdType: row.metaAdType ?? null },
      campaign ?? { metaAdCopy: { metaAdType: 'traffic' } },
      locationProfile,
    );
    if (adType === 'lead_gen') {
      sections.push({ id: 'bv-lead-form', label: 'Lead Form' });
    }
  } else {
    sections.push(
      { id: 'bv-headlines', label: 'Headlines' },
      { id: 'bv-descriptions', label: 'Descriptions' },
      { id: 'bv-path1', label: 'Path 1' },
      { id: 'bv-path2', label: 'Path 2' },
    );
  }
  sections.push(
    { id: 'bv-landing-url', label: 'Landing URL' },
    { id: 'bv-assets', label: 'Assets' },
    { id: 'bv-utm', label: 'UTM' },
    { id: 'bv-execution', label: 'Execution' },
  );
  return sections;
}

// ---------- internals ----------

function RowNotFound({
  rolloutId,
  platform,
}: {
  rolloutId: string;
  platform: 'meta' | 'google';
}) {
  const platformLabel = platform === 'meta' ? 'Meta' : 'Google';
  return (
    <div className="max-w-2xl mx-auto py-12 text-center">
      <h1 className="text-2xl font-semibold text-slate-900 mb-2">Row not found</h1>
      <p className="text-slate-600 mb-6">
        This row may have been deleted or removed from the rollout.
      </p>
      <Link
        to={`/rollouts/${rolloutId}/grid/${platform}`}
        className="text-blue-600 hover:underline"
      >
        Return to {platformLabel} grid
      </Link>
    </div>
  );
}

// Defensive shape parser for location.state.filteredRowIds. The runtime
// shape from React Router is opaque; we accept only a string[] of
// non-empty strings and otherwise treat as missing.
function parseFilteredRowIds(state: LocationState): string[] | null {
  if (state === null || state === undefined || typeof state !== 'object') return null;
  const candidate = (state as { filteredRowIds?: unknown }).filteredRowIds;
  if (!Array.isArray(candidate)) return null;
  const out: string[] = [];
  for (const v of candidate) {
    if (typeof v === 'string' && v.length > 0) out.push(v);
  }
  return out.length > 0 ? out : null;
}
